/** Published values, reviewed 11 October 2026. No annual or calendar-date interpolation. */
export const SEASON_SOURCES = {
  reliability: {
    label: "Spandre et al. (2019)",
    url: "https://tc.copernicus.org/articles/13/1325/2019/",
    detail:
      "Appendix A, Tables A1 (HIST), A2 and A3, groomed snow, 70% columns.",
    licence: "CC BY 4.0",
  },
  snowDays: {
    label: "Marty et al. (2017), Table S2",
    url: "https://tc.copernicus.org/articles/11/517/2017/tc-11-517-2017-supplement.pdf#page=3",
    detail:
      "Mean days with at least 5 cm of snow, SRES A2. Follow the supplement's mean label; the main text calls these medians.",
    licence: "CC BY 3.0",
  },
  futureTiming: {
    label: "Marty et al. (2017), section 3.5",
    url: "https://tc.copernicus.org/articles/11/517/2017/tc-11-517-2017.pdf#page=7",
    detail:
      "Aare catchment, 1,500 m, SRES A2. Approximate onset and end shifts in weeks relative to 1999–2012.",
    licence: "CC BY 3.0",
  },
  observedTiming: {
    label: "Klein et al. (2016)",
    url: "https://doi.org/10.1007/s10584-016-1806-y",
    detail:
      "Abstract. Eleven Swiss Alpine stations, 1,139–2,540 m, 1970–2015. Published mean onset and melt shifts, not a future extrapolation.",
    licence: "Published numerical facts; no source artwork reproduced",
  },
} as const;

export const SNOW_SCENARIOS = [
  { value: "rcp26", label: "Lower · RCP2.6", colour: "#16808f" },
  { value: "rcp45", label: "Intermediate · RCP4.5", colour: "#956318" },
  { value: "rcp85", label: "Very high · RCP8.5", colour: "#a64f35" },
] as const;
export type SnowScenario = (typeof SNOW_SCENARIOS)[number]["value"];

export const RELIABILITY_PERIODS = [
  { key: "reference", label: "1986–2005", kind: "Modelled reference" },
  { key: "near", label: "2030–2050", kind: "Near future" },
  { key: "late", label: "2080–2100", kind: "Late century" },
] as const;

type SnowMassif = {
  id: string;
  name: string;
  region: string;
  /** HIST, not SAFRAN, so reference and future use the climate-model ensemble. */
  reference: number;
  /** Tuple is [2030–2050, 2080–2100], metres above sea level. */
  projections: Record<SnowScenario, readonly [number, number]>;
};

/** 70% annual reliability quantile, not a confidence percentile. All selected values are below model ceiling sentinels. */
export const SNOW_MASSIFS = [
  {
    id: "chablais",
    name: "Chablais",
    region: "Northern French Alps",
    reference: 1580,
    projections: {
      rcp26: [1920, 1880],
      rcp45: [1860, 2060],
      rcp85: [1930, 2640],
    },
  },
  {
    id: "mont-blanc",
    name: "Mont Blanc",
    region: "Northern French Alps",
    reference: 1580,
    projections: {
      rcp26: [1900, 1810],
      rcp45: [1750, 1950],
      rcp85: [1830, 2570],
    },
  },
  {
    id: "haute-tarentaise",
    name: "Haute-Tarentaise",
    region: "Northern French Alps",
    reference: 1800,
    projections: {
      rcp26: [2100, 2160],
      rcp45: [1850, 2160],
      rcp85: [2030, 2670],
    },
  },
  {
    id: "pelvoux",
    name: "Pelvoux",
    region: "Southern French Alps",
    reference: 2020,
    projections: {
      rcp26: [2180, 2340],
      rcp45: [2190, 2450],
      rcp85: [2330, 2960],
    },
  },
  {
    id: "champsaur",
    name: "Champsaur",
    region: "Southern French Alps",
    reference: 2080,
    projections: {
      rcp26: [2260, 2440],
      rcp45: [2310, 2560],
      rcp85: [2420, 3120],
    },
  },
  {
    id: "haute-bigorre",
    name: "Haute-Bigorre",
    region: "French Pyrenees",
    reference: 1980,
    projections: {
      rcp26: [2280, 2180],
      rcp45: [2230, 2380],
      rcp85: [2300, 3200],
    },
  },
  {
    id: "haute-ariege",
    name: "Haute-Ariège",
    region: "French Pyrenees",
    reference: 1850,
    projections: {
      rcp26: [2140, 2050],
      rcp45: [2080, 2330],
      rcp85: [2120, 3010],
    },
  },
  {
    id: "esera",
    name: "Esera",
    region: "Spanish Pyrenees",
    reference: 2290,
    projections: {
      rcp26: [2620, 2610],
      rcp45: [2580, 2790],
      rcp85: [2660, 3260],
    },
  },
] as const satisfies readonly SnowMassif[];
export type SnowMassifId = (typeof SNOW_MASSIFS)[number]["id"];

export function snowReliabilitySeries(
  massif: SnowMassif,
  scenario: SnowScenario,
) {
  const values = [massif.reference, ...massif.projections[scenario]];
  return RELIABILITY_PERIODS.map((period, index) => ({
    ...period,
    elevationM: values[index],
  }));
}

export const SNOW_DAY_PERIODS = [
  { key: "reference", label: "1999–2012", name: "Modelled reference" },
  { key: "near", label: "2020–2049", name: "Early to mid-century" },
  { key: "middle", label: "2045–2074", name: "Mid-century" },
  { key: "late", label: "2070–2099", name: "Late century" },
] as const;
export type SnowDayPeriod = (typeof SNOW_DAY_PERIODS)[number]["key"];
type SnowDayStation = {
  id: string;
  name: string;
  region: string;
  days: Record<SnowDayPeriod, number>;
};

/** Exact Table S2 counts; deliberately no absolute start/end dates inferred from these counts. */
export const SNOW_DAY_STATIONS = [
  {
    id: "bern",
    name: "Bern",
    region: "Aare",
    days: { reference: 40, near: 13, middle: 5, late: 0 },
  },
  {
    id: "interlaken",
    name: "Interlaken",
    region: "Aare",
    days: { reference: 41, near: 14, middle: 6, late: 0 },
  },
  {
    id: "grindelwald",
    name: "Grindelwald",
    region: "Aare",
    days: { reference: 101, near: 72, middle: 37, late: 14 },
  },
  {
    id: "adelboden",
    name: "Adelboden",
    region: "Aare",
    days: { reference: 139, near: 107, middle: 75, late: 35 },
  },
  {
    id: "murren",
    name: "Mürren",
    region: "Aare",
    days: { reference: 173, near: 148, middle: 116, late: 77 },
  },
  {
    id: "chur",
    name: "Chur",
    region: "Grisons",
    days: { reference: 39, near: 14, middle: 5, late: 0 },
  },
  {
    id: "disentis",
    name: "Disentis",
    region: "Grisons",
    days: { reference: 98, near: 68, middle: 40, late: 18 },
  },
  {
    id: "davos",
    name: "Davos",
    region: "Grisons",
    days: { reference: 157, near: 123, middle: 86, late: 49 },
  },
  {
    id: "samedan",
    name: "Samedan",
    region: "Grisons",
    days: { reference: 165, near: 139, middle: 101, late: 61 },
  },
  {
    id: "weissfluhjoch",
    name: "Weissfluhjoch",
    region: "Grisons",
    days: { reference: 254, near: 228, middle: 197, late: 163 },
  },
] as const satisfies readonly SnowDayStation[];
export type SnowDayStationId = (typeof SNOW_DAY_STATIONS)[number]["id"];

export function snowDayComparison(
  station: SnowDayStation,
  period: SnowDayPeriod,
) {
  const reference = station.days.reference;
  const days = station.days[period];
  return {
    days,
    reference,
    lostDays: reference - days,
    retainedPercent: Math.round((days / reference) * 100),
  };
}

export const OBSERVED_SEASON_SHIFT = {
  startYear: 1970,
  endYear: 2015,
  stations: 11,
  onsetLaterDays: 12,
  meltEarlierDays: 26,
  durationTrendDaysPerDecade: -8.9,
} as const;

/** Approximate weeks quoted in section 3.5, Aare 1,500 m, SRES A2. Not the Table S2 station counts. */
export const PROJECTED_SEASON_SHIFTS = {
  near: { period: "2020–2049", onsetLaterWeeks: 2, meltEarlierWeeks: 2 },
  late: { period: "2070–2099", onsetLaterWeeks: 5, meltEarlierWeeks: 11 },
} as const;
